import React, { useEffect, useState } from 'react';
import Toast from '../../../components/ui/Toast';
import { getImageUrl } from '../../../utils/config';

const CategoryForm = ({ 
  category = null, 
  onSubmit, 
  onCancel, 
  isSubmitting = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    icon: null,
    remove_icon: false
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        icon: null,
        remove_icon: false
      });
    }
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        showToast('Please select a valid image file (JPG, JPEG, PNG, or SVG)', 'error');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showToast('Image size should be less than 2MB', 'error');
        return;
      }

      setFormData(prev => ({
        ...prev,
        icon: file,
        remove_icon: false // Reset remove_icon when new file is selected
      }));
    }
  };

  const handleRemoveIcon = () => {
    setFormData(prev => ({
      ...prev,
      icon: null,
      remove_icon: true
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // For create: name is required
    if (!category && !formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    // For both create and update: name validation
    if (formData.name.trim() && formData.name.trim().length > 255) {
      newErrors.name = 'Category name must not exceed 255 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const formDataToSend = new FormData();
      
      // Only send name if it's not empty
      if (formData.name.trim()) {
        formDataToSend.append('name', formData.name.trim());
      }
      
      // Send icon if selected (only if it's actually a file)
      if (formData.icon && formData.icon instanceof File) {
        formDataToSend.append('icon', formData.icon);
      }

      // Send remove_icon flag if true
      if (formData.remove_icon) {
        formDataToSend.append('remove_icon', '1');
      }

      await onSubmit(formDataToSend);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {category ? 'Edit Category' : 'Create New Category'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {category ? 'Update the category information below.' : 'Add a new category to your store.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Category Name {!category && '*'}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter category name"
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Category Icon */}
        <div>
          <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-2">
            Category Icon (Optional)
          </label>
          <div className="space-y-3">
            <input
              type="file"
              id="icon"
              name="icon"
              accept="image/jpeg,image/jpg,image/png,image/svg+xml"
              onChange={handleFileChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={isSubmitting}
            />
            
            {/* Current Icon Display */}
            {category?.icon && !formData.icon && !formData.remove_icon && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <img
                    src={getImageUrl(category.icon)}
                    alt="Current icon"
                    className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                  />
                  <span className="text-sm text-gray-600">Current icon</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveIcon}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove
                </button>
              </div>
            )}

            {/* New Icon Preview */}
            {formData.icon && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <img
                    src={URL.createObjectURL(formData.icon)}
                    alt="Preview"
                    className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                  />
                  <span className="text-sm text-gray-600">{formData.icon.name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, icon: null }))}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                >
                  Remove
                </button>
              </div>
            )}

            {/* Remove Icon Checkbox (for backend) */}
            {formData.remove_icon && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-lg">
                <input
                  type="checkbox"
                  id="remove_icon"
                  name="remove_icon"
                  checked={formData.remove_icon}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <label htmlFor="remove_icon" className="text-sm text-red-700">
                  Remove current icon
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {category ? 'Updating...' : 'Creating...'}
              </div>
            ) : (
              category ? 'Update Category' : 'Create Category'
            )}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={closeToast}
      />
    </div>
  );
};

export default CategoryForm; 